import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Heart, Users, Crown, Loader2, Lock, TrendingUp, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const getImageUrl = (photoPath) => {
  if (!photoPath) return 'https://images.pexels.com/photos/1040881/pexels-photo-1040881.jpeg?auto=compress&cs=tinysrgb&w=300';
  if (photoPath.startsWith('http')) return photoPath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${photoPath}`;
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} min siden`;
  if (diffHours < 24) return `${diffHours} timer siden`;
  if (diffDays === 1) return 'I går';
  return `${diffDays} dage siden`;
};

const ActivityFeed = () => {
  const [viewers, setViewers] = useState([]);
  const [stats, setStats] = useState({ views_this_week: 0, likes_this_week: 0, matches_this_week: 0 });
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [viewsRes, statsRes] = await Promise.all([
          axios.get(`${API}/activity/views`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/activity/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setViewers(viewsRes.data.viewers);
        setIsPremium(viewsRes.data.is_premium);
        setStats(statsRes.data);
      } catch (err) {
        toast.error('Kunne ikke hente aktivitet');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-primary" />
              <span className="text-lg font-display font-semibold text-foreground">Aktivitet</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card rounded-2xl border border-border p-4 text-center"
            >
              <Eye className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{stats.views_this_week}</div>
              <div className="text-xs text-muted-foreground">Profilvisninger</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-card rounded-2xl border border-border p-4 text-center"
            >
              <Heart className="w-6 h-6 text-primary mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{stats.likes_this_week}</div>
              <div className="text-xs text-muted-foreground">Likes</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-2xl border border-border p-4 text-center"
            >
              <Users className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-foreground">{stats.matches_this_week}</div>
              <div className="text-xs text-muted-foreground">Matches</div>
            </motion.div>
          </div>

          {/* Premium upsell if not premium */}
          {!isPremium && viewers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-4 mb-6"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Crown className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-foreground">Se hvem der har set din profil</h4>
                  <p className="text-sm text-muted-foreground">Opgrader til Premium for at se alle</p>
                </div>
                <Button onClick={() => navigate('/premium')} size="sm" className="rounded-full">
                  Opgrader
                </Button>
              </div>
            </motion.div>
          )}

          {/* Viewers Section */}
          <div className="mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2 mb-4">
              <Eye className="w-5 h-5 text-primary" />
              Hvem har set din profil
              <Badge variant="secondary">{viewers.length}</Badge>
            </h3>

            {viewers.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-10 h-10 text-muted-foreground" />
                </div>
                <h4 className="text-lg font-semibold text-foreground">Ingen visninger endnu</h4>
                <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                  Når nogen ser din profil, vil de vises her
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {viewers.map((viewer, index) => (
                  <motion.div
                    key={viewer.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center gap-3 p-3 bg-card rounded-xl border border-border ${
                      !viewer.is_blurred ? 'cursor-pointer hover:bg-muted/50' : ''
                    }`}
                    onClick={() => !viewer.is_blurred && navigate(`/profile/${viewer.id}`)}
                  >
                    <div className="relative">
                      <img
                        src={getImageUrl(viewer.photos?.[0])}
                        alt={viewer.name}
                        className={`w-14 h-14 rounded-full object-cover ${
                          viewer.is_blurred ? 'blur-lg' : ''
                        }`}
                      />
                      {viewer.is_blurred && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h4 className={`font-semibold text-foreground ${viewer.is_blurred ? 'blur-sm' : ''}`}>
                        {viewer.name}{viewer.age ? `, ${viewer.age}` : ''}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {formatTimeAgo(viewer.viewed_at)}
                      </p>
                    </div>
                    
                    {!viewer.is_blurred && (
                      <Button variant="outline" size="sm" className="rounded-full">
                        Se profil
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ActivityFeed;
